import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Chip,
  CircularProgress,
  Divider as MuiDivider,
  Drawer,
  Link,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { format } from "date-fns";
import useAuth from "../../../../hooks/useAuth";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import useAppSelector from "../../../../hooks/useAppSelector";
import {
  BankTransaction,
  fetchUnmatched,
  ignoreBankTransaction,
  manualReconcile,
} from "../../../../redux/slices/bankTransactions";
import axiosInstance from "../../../../utils/axios";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Button = styled(MuiButton)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

interface InvoiceSearchResult {
  id: number;
  invoiceNumber: string;
  total: number;
  dueDate: string;
  status: string;
  user?: { firstName: string; lastName: string };
  referenceNumber?: string | null;
}

function ReconcileDrawer({
  tx,
  associationId,
  onClose,
  onDone,
}: {
  tx: BankTransaction;
  associationId: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const dispatch = useAppDispatch();
  const [query, setQuery] = useState("");
  const [invoices, setInvoices] = useState<InvoiceSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoiceSearchResult | null>(null);
  const [amount, setAmount] = useState<string>(String(tx.amount));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountNum = parseFloat(amount) || 0;
  const amountMismatch =
    selectedInvoice !== null &&
    Math.round(amountNum * 100) !== Math.round(selectedInvoice.total * 100);
  const isPartial =
    selectedInvoice !== null &&
    Math.round(amountNum * 100) < Math.round(selectedInvoice.total * 100);

  useEffect(() => {
    if (!query || query.length < 2) {
      setInvoices([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axiosInstance.get(
          "/invoices/association/" + associationId
        );
        const all: InvoiceSearchResult[] = res.data ?? [];
        const q = query.toLowerCase();
        setInvoices(
          all
            .filter(
              (inv) =>
                inv.invoiceNumber?.toLowerCase().includes(q) ||
                inv.referenceNumber?.toLowerCase().includes(q) ||
                inv.user?.firstName?.toLowerCase().includes(q) ||
                inv.user?.lastName?.toLowerCase().includes(q)
            )
            .slice(0, 10)
        );
      } catch {
        setInvoices([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, associationId]);

  const handleReconcile = async () => {
    if (!selectedInvoice) return;
    setSaving(true);
    setError(null);
    try {
      await dispatch(
        manualReconcile({
          id: tx.id,
          associationId,
          invoiceId: selectedInvoice.id,
          amount: Math.round(amountNum * 100),
        })
      ).unwrap();
      onDone();
    } catch (e: any) {
      setError(e?.message ?? "Kohdistus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  const handleIgnore = async () => {
    setSaving(true);
    setError(null);
    try {
      await dispatch(
        ignoreBankTransaction({ id: tx.id, associationId })
      ).unwrap();
      onDone();
    } catch (e: any) {
      setError(e?.message ?? "Ohitus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        width: 420,
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h6">Manuaalinen kohdistus</Typography>

      {/* Transaction info */}
      <Card variant="outlined">
        <CardContent py={2} px={2}>
          <Typography variant="subtitle2" gutterBottom>
            Pankkitapahtuma
          </Typography>
          <Typography variant="body2">
            Päivämäärä: {format(new Date(tx.bookingDate), "dd.MM.yyyy")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: tx.amount >= 0 ? "success.main" : "error.main" }}
          >
            Summa: {tx.amount >= 0 ? "+" : ""}
            {tx.amount.toFixed(2)} {tx.currency}
          </Typography>
          {tx.debtorName && (
            <Typography variant="body2">Maksaja: {tx.debtorName}</Typography>
          )}
          {tx.referenceNumber && (
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", mt: 0.5 }}
            >
              Viite: {tx.referenceNumber}
            </Typography>
          )}
          {tx.remittanceInfo && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {tx.remittanceInfo}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Invoice search */}
      <Typography variant="subtitle2">Hae lasku</Typography>
      <TextField
        size="small"
        label="Viitenumero, laskunumero tai nimi"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setSelectedInvoice(null);
        }}
        fullWidth
      />

      {searchLoading && <CircularProgress size={20} />}

      {invoices.length > 0 && !selectedInvoice && (
        <Paper variant="outlined" sx={{ maxHeight: 220, overflowY: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Numero</TableCell>
                <TableCell>Saaja</TableCell>
                <TableCell align="right">Summa (€)</TableCell>
                <TableCell>Tila</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedInvoice(inv);
                    setAmount(String(tx.amount));
                  }}
                >
                  <TableCell>{inv.invoiceNumber}</TableCell>
                  <TableCell>
                    {inv.user
                      ? `${inv.user.firstName} ${inv.user.lastName}`
                      : "—"}
                  </TableCell>
                  <TableCell align="right">{inv.total.toFixed(2)}</TableCell>
                  <TableCell>{inv.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {selectedInvoice && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Alert
            severity="info"
            action={
              <Button size="small" onClick={() => setSelectedInvoice(null)}>
                Vaihda
              </Button>
            }
          >
            Lasku #{selectedInvoice.invoiceNumber} —{" "}
            {selectedInvoice.user
              ? `${selectedInvoice.user.firstName} ${selectedInvoice.user.lastName}`
              : "Ei tiedossa"}{" "}
            — {selectedInvoice.total.toFixed(2)} €
          </Alert>

          {amountMismatch && (
            <Alert severity="warning">
              Summat eivät täsmää. Voit silti kohdistaa — syötä oikea summa
              alle.
            </Alert>
          )}

          <TextField
            size="small"
            label="Kohdistettava summa (€)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            inputProps={{ step: "0.01", min: "0.01" }}
          />

          {isPartial && (
            <Typography variant="caption" color="text.secondary">
              Osittainen maksu — lasku merkitään "Osittain maksettu"
            </Typography>
          )}
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedInvoice || saving || amountNum <= 0}
          onClick={handleReconcile}
        >
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : isPartial ? (
            "Täsmäytä osittain"
          ) : (
            "Täsmäytä"
          )}
        </Button>
        <Button
          variant="outlined"
          color="warning"
          disabled={saving}
          onClick={handleIgnore}
        >
          Ohita
        </Button>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Peruuta
        </Button>
      </Box>
    </Box>
  );
}

export default function Unmatched() {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { transactions, status } = useAppSelector(
    (state) => state.bankTransactions
  );

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const [selectedTx, setSelectedTx] = useState<BankTransaction | null>(null);

  const loadUnmatched = () => {
    if (associationId) dispatch(fetchUnmatched(associationId));
  };

  useEffect(() => {
    loadUnmatched();
  }, [dispatch, associationId]);

  const isLoading = status === "loading" && transactions.length === 0;

  const handleDone = () => {
    setSelectedTx(null);
    loadUnmatched();
  };

  return (
    <>
      <Helmet title="Täsmäyttämättömät tapahtumat" />
      <Typography variant="h3" gutterBottom>
        Täsmäyttämättömät tapahtumat
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/accounting/bank/transactions">
          Talous
        </Link>
        <Typography>Täsmäyttämättömät</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Card mb={6}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Klikkaa riviä avataksesi manuaalisen kohdistuspaneelin.
          </Typography>
        </CardContent>
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Päivämäärä</TableCell>
                <TableCell align="right">Summa (€)</TableCell>
                <TableCell>Maksaja</TableCell>
                <TableCell>Viitenumero</TableCell>
                <TableCell>Viesti</TableCell>
                <TableCell>Tila</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Ei täsmäyttämättömiä tapahtumia
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => (
                  <TableRow
                    key={tx.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => setSelectedTx(tx)}
                  >
                    <TableCell>
                      {format(new Date(tx.bookingDate), "dd.MM.yyyy")}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "success.main", fontWeight: 500 }}
                    >
                      +{tx.amount.toFixed(2)}
                    </TableCell>
                    <TableCell>{tx.debtorName ?? "—"}</TableCell>
                    <TableCell>
                      {tx.referenceNumber ? (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {tx.referenceNumber}
                        </Typography>
                      ) : (
                        <Chip size="small" label="Ei viitettä" color="error" />
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tx.remittanceInfo ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label="Täsmäyttämätön"
                        color="warning"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Card>

      <Drawer
        anchor="right"
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
      >
        {selectedTx && (
          <ReconcileDrawer
            tx={selectedTx}
            associationId={associationId}
            onClose={() => setSelectedTx(null)}
            onDone={handleDone}
          />
        )}
      </Drawer>
    </>
  );
}
